import * as React from 'react';
import { translate } from 'react-i18next';
import * as c from 'models/constants';
import { ICart, ICartAction, ICartObject } from 'models/cart/cart';
import { InjectedRouter } from 'react-router';
import { IStore } from 'redux/IStore';
import { PageTitle, PortalPageSection, ButtonsSection } from 'components/PortalStructure';
import { Button, ShopData, ItemCardList, ErrorSummary, WizzardSteps, WhiteButton, CartContainer } from 'components';
import { IItemCardList } from 'models/itemcard';
import { ICheckoutShopData } from 'models/cart/checkoutshopdata';
import { IShoppingBasketItem } from 'models/cart/shoppingbasketitem';
import { IShopShippingOption } from 'models/shop/shopshippingoption';
import { setShippingOption, setShippingOptions } from 'modules/cart';
import { formatCurrency } from 'helpers/common';
import { reduxForm, getFormSyncErrors, Field } from 'redux-form';
import { required } from 'helpers/validationattributes';
import { CartShippingOptionsRadioGroup } from 'components/Cart/CartShippingOptionsRadioGroup';
import { IProductQtyUpdateRequest } from 'models/cart/productQtyUpdateRequest';
import { ShoppingBagRemovedItems } from '../../../components/Cart/ShoppingBagRemovedItems';
import { getProductCard } from 'redux/modules/product/productcard';
import { IProductCardDataAction } from 'models/product/productcard';
const { connect } = require('react-redux');

require('./style.css');

interface IProps {
	currentFormValues: ICartObject;
	initialValues: ICartObject;
	cart?: ICart;
	location?: any;
	router: InjectedRouter;
	route: any;

	/* form props */
	pristine: boolean;
	dirty: boolean;
	submitting: boolean;
	handleSubmit: any;
	submitFailed: boolean;
	formErrors?: any;

	/* functions props */
	t?: (key: string, options?: any) => string;
	nextPage?: () => void;
	previousPage?: () => void;
	setShippingOption?: (shippingOptionId: string, items: string[]) => ICartAction;
	setShippingOptions?: () => ICartAction;
	change: (field: string, value: any) => void;
	removeShoppingBasketItem?: (itemId: string) => ICartAction;
	updateShoppingBasketItemQty?: (productQtyUpdateRequest: IProductQtyUpdateRequest) => ICartAction;
	getProductCard?: (id: string, target: string) => IProductCardDataAction;
}

interface ICheckoutShippingOptionsItemCardList extends IItemCardList {
	currencyKey?: number;
}

@translate(c.DEFAULT_TRANSLATION_NAMESPACE)
class CheckoutShippingOptionsForm extends React.Component<IProps, any> {
	constructor(props) {
		super(props);

		this.handleShippingOptionChange = this.handleShippingOptionChange.bind(this);
		this.removeShoppingBasketItem = this.removeShoppingBasketItem.bind(this);
		this.handleOnProductQtyDropdownChange = this.handleOnProductQtyDropdownChange.bind(this);
		this.onSubmit = this.onSubmit.bind(this);
	}

	public componentDidMount() {
		this.props.setShippingOptions();
	}

	private handleShippingOptionChange(shippingOptionId: string, items: string[]) {
		this.props.setShippingOption(shippingOptionId, items);
	}

	private removeShoppingBasketItem(productId: string) {
		this.props.removeShoppingBasketItem(productId);
	}

	private handleOnProductQtyDropdownChange(productQtyUpdateRequest: IProductQtyUpdateRequest) {
		this.props.updateShoppingBasketItemQty(productQtyUpdateRequest);
	}

	public onSubmit() {
		this.props.nextPage();
	}

	public render() {
		const { t, handleSubmit, change } = this.props;
		const { shippingName, shippingAddressLine, shippingZIPCode, shippingCity, shippingCountryKey } =
			this.props.currentFormValues.order;
		const { shoppingBasket } = this.props.cart;

		const errorSummaryProps = {
			error: this.props.cart ? this.props.cart.error : false,
			message: this.props.cart ? this.props.cart.message : null,
		};

		let disableContinueButton: boolean = false;

		const countryText = t(`DV_Countries_${shippingCountryKey}`);
		const fullShippingAddress = `${shippingName}, ${shippingAddressLine}, ${shippingZIPCode} ${shippingCity}, ${countryText}`;

		let itemsContent = null;
		let removedItems: IShoppingBasketItem[] = [];
		if (shoppingBasket && shoppingBasket.items && shoppingBasket.items.length > 0) {
			const items: IShoppingBasketItem[] = shoppingBasket.items;
			removedItems = items.filter((x) => x.shopId === null);
			const shopIds: string[] = shoppingBasket.shopGroupTotals.map((x) => {
				return x.shopId;
			});

			shopIds.sort((a, b) => {
				if (a < b) {
					return -1;
				}
				if (a > b) {
					return 1;
				}

				return 0;
			});

			itemsContent = shopIds.map((shopId) => {
				const shopItems: IShoppingBasketItem[] = items
					.filter((x) => x.shopId === shopId)
					.sort((a, b) => {
						if (a.name < b.name) {
							return -1;
						}
						if (a.name > b.name) {
							return 1;
						}

						return 0;
					});

				if (shopItems && shopItems.length > 0) {
					const listProps: ICheckoutShippingOptionsItemCardList = {
						items: shopItems,
						itemType: c.NODE_TYPE_SHOPPINGBAG_ITEM_CARD,
						currencyKey: shoppingBasket.currencyKey,
						deleteItem: this.removeShoppingBasketItem,
						customFunction: this.handleOnProductQtyDropdownChange,
						goToCard: (id: string, target) => this.props.getProductCard(id, target),
					};

					const getLargestShippingPrice = (item: IShoppingBasketItem | null): number => {
						if (!item) {
							return 0;
						}
						const prices = (item.shippingOptions || []).map((opt) => opt.price);
						return Math.max.apply(null, prices) || 0;
					};

					const largestItem = shopItems.reduce<IShoppingBasketItem | null>((acc, val) => {
						const accPrice = getLargestShippingPrice(acc);
						const valPrice = getLargestShippingPrice(val);
						return valPrice > accPrice ? val : acc;
					}, shopItems[0]);

					const shopData: ICheckoutShopData = {
						name: largestItem.shopName,
						location: largestItem.shopLocation,
						picture: largestItem.shopPicture,
					};

					const shippingOptions: IShopShippingOption[] = largestItem.shippingOptions;
					const productIds = shopItems.map((item) => item.productId);

					const availableShippingOptions: IShopShippingOption[] = [];
					if (largestItem.shippingOptions) {
						shippingOptions.forEach((shippingOption) => {
							const availableToCountries = shippingOption.availableTo.split(',').map((countryKeyString) => {
								return parseInt(countryKeyString, 10);
							});

							if (availableToCountries.find((x) => x === shippingCountryKey)) {
								availableShippingOptions.push(shippingOption);
							}
						});
					}

					if (availableShippingOptions.length === 0) {
						disableContinueButton = true;
					}

					const shippingUnavailable = availableShippingOptions.length === 0 && largestItem.shippingOptions;

					const shopGroupTotal = shoppingBasket.shopGroupTotals.find((x) => x.shopId === shopId).itemsTotal;

					return (
						<div key={shopId}>
							<PortalPageSection>
								<ShopData data={shopData} />
							</PortalPageSection>
							<PortalPageSection>
								<ItemCardList {...listProps} />
							</PortalPageSection>
							{availableShippingOptions.length > 0 ? (
								<PortalPageSection>
									<Field
										component={CartShippingOptionsRadioGroup}
										name={`${largestItem.shopCode}shippingOptionId`}
										shippingOptions={availableShippingOptions}
										validate={[required]}
										currencyKey={shoppingBasket.currencyKey}
										onChangeAction={(e: any) => {
											this.handleShippingOptionChange(e.target.value, productIds);
										}}
										selectedShippingOptionId={largestItem.shippingOptionId}
										change={change}
										shopGroupTotal={shopGroupTotal}
									/>
								</PortalPageSection>
							) : null}
							<PortalPageSection>
								<div className="shippingTo">
									{t('Cart_CheckoutShippingOptions_ShippingTo')}{' '}
									<span className="color" style={{ color: shippingUnavailable ? 'red' : 'black' }}>
										{fullShippingAddress}
									</span>
								</div>
							</PortalPageSection>
							{shippingUnavailable ? (
								<div className="errorBackgroundRed">
									<span className="textErrorWhite">{t('Cart_CheckoutShippingOptions_CanNotBeShipped')}</span>
								</div>
							) : null}
						</div>
					);
				} else {
					return null;
				}
			});
		}

		return (
			<div className="cartCheckoutShippingOptions">
				<PageTitle title={t('BSPortal_Common_Shipping')} />
				<CartContainer>
					<div className="shippingOptionsStep">
						<div className="profileError">
							<ErrorSummary {...errorSummaryProps} />
						</div>
						<form onSubmit={handleSubmit(this.onSubmit)}>
							<ShoppingBagRemovedItems items={removedItems} removeShoppingBasketItem={this.removeShoppingBasketItem} />
							{itemsContent}
							<ButtonsSection>
								<div className="row">
									<div className="col-sm-6">
										<WizzardSteps currentStep={2} numberOfSteps={3} />
									</div>
									<div className="col-sm-6">
										<div className="floatRight">
											<div className="priceTotalBottom">
												<span className="wrapperTotalPrice">
													{t('BSPortal_Common_Total')}:{' '}
													{formatCurrency(shoppingBasket.currencyKey, shoppingBasket.total)}
												</span>
											</div>
											<div className="buttonActions">
												<WhiteButton type="button" onClick={this.props.previousPage}>
													{t('BSPortal_Common_Back')}
												</WhiteButton>
												<Button type="submit" disabled={disableContinueButton}>
													{t('BSPortal_Common_Continue')}
												</Button>
											</div>
											<div className="clear" />
										</div>
										<div className="clear" />
									</div>
								</div>
							</ButtonsSection>
						</form>
					</div>
				</CartContainer>
			</div>
		);
	}
}

let CheckoutShippingOptions = reduxForm({
	form: c.FORM_NAME_CART,
	destroyOnUnmount: false,
	forceUnregisterOnUnmount: true,
})(CheckoutShippingOptionsForm);

CheckoutShippingOptions = connect(
	(state: IStore) => {
		let currentFormValues: ICartObject = null;
		if (state.form[c.FORM_NAME_CART] && state.form[c.FORM_NAME_CART].values) {
			currentFormValues = state.form[c.FORM_NAME_CART].values;
		} else {
			currentFormValues = state.cart.cart;
		}

		return {
			initialValues: state.cart.cart,
			cart: state.cart,
			currentFormValues,
			formErrors: getFormSyncErrors(c.FORM_NAME_CART)(state),
		};
	},
	(dispatch) => ({
		setShippingOption: (shippingOptionId: string, items: string[]) =>
			dispatch(setShippingOption(shippingOptionId, items)),
		setShippingOptions: () => dispatch(setShippingOptions()),
		getProductCard: (id: string, target: string) => dispatch(getProductCard(id, target)),
	}),
)(CheckoutShippingOptions);

export { CheckoutShippingOptions };
